import type { DocumentationSection } from '../types/documentation.types'

export const documentationSections: DocumentationSection[] = [
  {
    id: 'installation',
    title: 'Installation',
    items: [
      {
        id: 'sdk-installation',
        title: 'SDK Installation',
        searchKeywords: ['install', 'setup', 'npm', 'pip', 'package'],
        content: {
          type: 'tabbed',
          description:
            'Install the Coobird SDK to start tracking agent events and costs.',
          tabs: [
            {
              id: 'web',
              label: 'Node.js / TypeScript',
              content:
                'Install the official Node.js SDK for server-side applications.',
              steps: [
                {
                  id: 'install-npm',
                  title: 'Install the package',
                  description:
                    'Add the Coobird SDK to your project using npm, pnpm, or yarn.',
                  codeExample: {
                    language: 'bash',
                    code: `npm install @coobird-ai/sdk
# or
pnpm add @coobird-ai/sdk`,
                  },
                },
                {
                  id: 'basic-setup',
                  title: 'Basic setup',
                  description:
                    'Initialize the SDK with your API key and host URL.',
                  codeExample: {
                    language: 'typescript',
                    code: `import { Coomon } from '@coobird-ai/sdk'

const coomon = new Coomon(process.env.COOMON_API_KEY!, {
  host: 'https://app.coobird.ai',  // your deployment URL
})`,
                  },
                },
              ],
            },
            {
              id: 'http',
              label: 'HTTP API',
              content: 'Use the HTTP API directly from any language.',
              steps: [
                {
                  id: 'http-example',
                  title: 'Send events via HTTP',
                  description:
                    'Call the bulk events endpoint with an X-API-Key header.',
                  codeExample: {
                    language: 'bash',
                    code: `curl -X POST https://app.coobird.ai/api/v1/events/bulk \\
  -H "X-API-Key: sk_live_abc123" \\
  -H "Content-Type: application/json" \\
  -d '{
    "events": [{
      "customer_id": "cus_acme",
      "action_type": "task",
      "quantity": 1,
      "attributes": { "action": "email_sent" }
    }]
  }'`,
                  },
                },
              ],
            },
          ],
        },
      },
    ],
  },
  {
    id: 'getting-started',
    title: 'Getting Started',
    items: [
      {
        id: 'first-signal',
        title: 'Send your first signal',
        searchKeywords: ['first', 'start', 'begin', 'track', 'signal'],
        content: {
          type: 'simple',
          description: 'Learn how to send your first tracking signal.',
          content: `Once you've installed the SDK, you can start tracking agent events. Events are queued in memory and sent in batches automatically.`,
          codeExamples: [
            {
              language: 'typescript',
              description: 'Track a simple task completion (queued)',
              code: `// Track when an agent sends an email — fire-and-forget
coomon.trackTask({
  customerId: 'cus_acme',
  quantity: 1,
  attributes: { action: 'email_sent' },
  metadata: { userId: 'user_jane', sessionId: 'sess_abc123' },
})`,
            },
            {
              language: 'typescript',
              description: 'Serverless: send immediately and await delivery',
              code: `// In AWS Lambda / Vercel / Cloudflare Workers,
// use trackImmediate to ensure delivery before the process exits.
await coomon.trackTaskImmediate({
  customerId: 'cus_acme',
  quantity: 1,
  attributes: { action: 'email_sent' },
})`,
            },
          ],
        },
      },
      {
        id: 'configuration',
        title: 'Configuration',
        searchKeywords: [
          'config',
          'settings',
          'environment',
          'api key',
          'host',
          'url',
        ],
        content: {
          type: 'simple',
          description: 'Configure the SDK for your environment and needs.',
          content: `The SDK needs two things: an API key and a host URL. Create an API key from Settings > API Keys. The host is your deployment URL — the SDK appends /api/v1/ internally, so just pass the base domain.`,
          codeExamples: [
            {
              language: 'typescript',
              description: 'Full configuration options',
              code: `const coomon = new Coomon(process.env.COOMON_API_KEY!, {
  host: 'https://app.coobird.ai', // your deployment URL
  flushAt: 20,                    // flush after this many queued events
  flushInterval: 10_000,          // flush every N ms (0 to disable timer)
  maxRetries: 3,                  // retry attempts on retriable failures
  maxBatchSize: 100,              // max events per HTTP request
  maxQueueSize: 1000,             // max events in memory (oldest dropped)
  requestTimeout: 10_000,         // per-request timeout in ms
  shutdownTimeoutMs: 30_000,      // max wait during shutdown()
  debug: false,                   // enable verbose [coomon] logging
  disabled: false,                // set true in tests/CI
  onError: (err) => {             // called on unrecoverable send errors
    console.error('[coomon]', err)
  },
})`,
            },
            {
              language: 'typescript',
              description: 'Connecting to different environments',
              code: `// Local development
const coomon = new Coomon('sk_live_abc123', {
  host: 'http://localhost:4444',
})

// Staging
const coomon = new Coomon('sk_live_abc123', {
  host: 'https://dev.coobird.ai',
})

// Production
const coomon = new Coomon('sk_live_abc123', {
  host: 'https://app.coobird.ai',
})`,
            },
          ],
        },
      },
    ],
  },
  {
    id: 'signal-types',
    title: 'Signal Types',
    items: [
      {
        id: 'task-signals',
        title: 'Task Signals',
        searchKeywords: ['task', 'action', 'individual', 'email', 'message'],
        content: {
          type: 'simple',
          description: 'Track individual actions performed by your agents.',
          content: `Task signals represent individual actions that agents perform. These are the building blocks of agent activity tracking.`,
          codeExamples: [
            {
              language: 'typescript',
              description: 'Examples of task signals',
              code: `// 3 emails sent by agent
coomon.trackTask({
  customerId: 'cus_acme',
  quantity: 3,
  attributes: { action: 'email_sent' },
  metadata: { workflow: 'prospect_outreach', sessionId: 'sess_123' },
});

// LLM call — bill by token usage
coomon.trackTask({
  customerId: 'cus_techcorp',
  attributes: {
    model: 'gpt-4o',
    input_tokens: 800,
    output_tokens: 150,
  },
});`,
            },
          ],
        },
      },
      {
        id: 'workflow-signals',
        title: 'Workflow Signals',
        searchKeywords: ['workflow', 'process', 'sequence', 'multi-step'],
        content: {
          type: 'simple',
          description: 'Track completion of multi-step agent processes.',
          content: `Workflow signals represent the completion of complex, multi-step processes that agents execute.`,
          codeExamples: [
            {
              language: 'typescript',
              description: 'Tracking workflow completion',
              code: `coomon.trackWorkflow({
  customerId: 'cus_acme',
  quantity: 1,
  attributes: {
    workflow_id: 'prospect_outreach',
    steps_completed: 5,
    duration_ms: 720_000,
    leads_contacted: 10,
    qualified_leads: 7,
  },
  metadata: { sessionId: 'sess_abc123', userId: 'user_jane' },
});`,
            },
          ],
        },
      },
      {
        id: 'outcome-signals',
        title: 'Outcome Signals',
        searchKeywords: [
          'outcome',
          'result',
          'achievement',
          'success',
          'meeting',
        ],
        content: {
          type: 'simple',
          description:
            'Track successful results or business value delivered by agents.',
          content: `Outcome signals represent successful results or business value that agents deliver.`,
          codeExamples: [
            {
              language: 'typescript',
              description: 'Tracking successful outcomes',
              code: `coomon.trackOutcome({
  customerId: 'cus_acme',
  quantity: 1,
  attributes: {
    outcome_type: 'meeting_booked',
    meeting_type: 'product_demo',
    deal_value: 50000,
    currency: 'usd',
  },
  metadata: {
    sessionId: 'sess_abc123',
    userId: 'user_jane',
    campaignId: 'camp_q3_enterprise',
    sourceWorkflow: 'prospect_outreach',
  },
});`,
            },
          ],
        },
      },
      {
        id: 'cost-signals',
        title: 'Cost Signals',
        searchKeywords: ['cost', 'expense', 'provider', 'openai', 'tokens'],
        content: {
          type: 'simple',
          description: 'Track internal costs incurred while serving agents.',
          content: `Cost signals help you track the expenses associated with running your agents, enabling margin calculation.`,
          codeExamples: [
            {
              language: 'typescript',
              description:
                'Tracking AI provider costs via signalCode fast path',
              code: `// Use signalCode when you have a pre-calculated cost.
// The enricher reads cost_usd directly from attributes,
// skipping the agent→signal lookup entirely.
coomon.track({
  customerId: 'cus_acme',
  actionType: 'task',
  signalCode: 'openai_llm_cost',
  attributes: {
    cost_usd: 0.0069,
    provider: 'openai',
    model: 'gpt-4o',
    input_tokens: 1900,
    output_tokens: 414,
  },
  metadata: { sessionId: 'sess_abc123', apiCallId: 'req_openai_xyz789' },
});`,
            },
          ],
        },
      },
    ],
  },
  {
    id: 'management',
    title: 'Customer & Wallet Management',
    items: [
      {
        id: 'create-customer',
        title: 'Create Customer',
        searchKeywords: [
          'customer',
          'create',
          'provision',
          'createCustomer',
          'company',
          'agent',
          'individual',
        ],
        content: {
          type: 'simple',
          description: 'Provision customers programmatically via the SDK.',
          content: `Use createCustomer() to create billing customers in Coomon. Unlike track(), this method is async and throws on failure — always await it and wrap in try/catch.`,
          codeExamples: [
            {
              language: 'typescript',
              description: 'Create a customer',
              code: `const customer = await coomon.createCustomer({
  name: 'Acme Corp',
  type: 'company',             // 'agent' | 'individual' | 'company'
  status: 'active',            // optional, default: 'active'
  email: 'billing@acme.com',
  externalId: 'acme-001',     // your system's ID for idempotent lookups
  currency: 'USD',
  metadata: { plan: 'pro' },
})
console.log(customer.id) // 'cust_abc123'`,
            },
          ],
        },
      },
      {
        id: 'create-contract',
        title: 'Create Contract',
        searchKeywords: [
          'contract',
          'create',
          'provision',
          'createContract',
          'plan',
          'billing',
          'subscription',
        ],
        content: {
          type: 'simple',
          description: 'Attach a customer to a plan with a billing contract.',
          content: `Use createContract() to subscribe a customer to a plan. All pricing, agent actions, and charges are copied from the plan server-side — you never re-specify them. The response includes nested plan and customer objects.`,
          codeExamples: [
            {
              language: 'typescript',
              description: 'Create a contract',
              code: `const contract = await coomon.createContract({
  planId: 'plan_abc123',
  customerId: customer.id,
  name: 'Acme Pro Contract',
  startDate: '2026-03-01',         // YYYY-MM-DD
  billingInterval: 'monthly',      // optional — overrides plan default
  collectionMethod: 'charge_automatically',
  metadata: { signed_by: 'john@acme.com' },
})
console.log(contract.id)        // 'cont_xyz789'
console.log(contract.plan.name) // 'Agent Platform Pro'`,
            },
            {
              language: 'typescript',
              description: 'Full provisioning flow with error handling',
              code: `try {
  const customer = await coomon.createCustomer({ name: 'Acme', type: 'company' })
  const contract = await coomon.createContract({
    planId: 'plan_abc123',
    customerId: customer.id,
    name: 'Acme Pro',
    startDate: '2026-03-01',
  })
  console.log('Provisioned:', contract.id)
} catch (err) {
  logger.error('Provisioning failed', { err })
}`,
            },
          ],
        },
      },
      {
        id: 'create-wallet',
        title: 'Create Wallet',
        searchKeywords: [
          'wallet',
          'create',
          'createWallet',
          'credits',
          'balance',
          'prepaid',
        ],
        content: {
          type: 'simple',
          description: 'Create a prepaid credit wallet for a customer.',
          content: `Use createWallet() to provision a credit wallet for a customer. Wallets hold a credits balance that is drawn down as agents consume resources.`,
          codeExamples: [
            {
              language: 'typescript',
              description: 'Create a wallet',
              code: `const wallet = await coomon.createWallet({
  customerId: customer.id,
  name: 'Main Wallet',
  currency: 'USD',
  initialCredits: 100,           // seed balance on creation
  conversionRate: 1.0,           // 1 monetary unit = N credits
  autoRechargeEnabled: true,
  autoRechargeThreshold: 20,     // top up when balance drops below 20
  autoRechargeAmount: 100,       // add 100 credits each time
  allowNegativeBalance: false,
  metadata: { source: 'onboarding' },
})
console.log(wallet.id)             // 'wal_abc123'
console.log(wallet.credits_balance) // 100`,
            },
          ],
        },
      },
      {
        id: 'wallet-balance',
        title: 'Wallet Balance',
        searchKeywords: [
          'wallet',
          'balance',
          'getWallet',
          'getWalletBalance',
          'credits',
        ],
        content: {
          type: 'simple',
          description: 'Fetch a wallet and its current balance.',
          content: `Use getWallet() to retrieve full wallet details or getWalletBalance() for a lightweight balance snapshot.`,
          codeExamples: [
            {
              language: 'typescript',
              description: 'Fetch wallet and balance',
              code: `// Full wallet object
const wallet = await coomon.getWallet('wal_abc123')
console.log(wallet.status)          // 'active'
console.log(wallet.credits_balance) // 70

// Lightweight balance snapshot
const balance = await coomon.getWalletBalance('wal_abc123')
console.log(balance.credits_balance) // 70
console.log(balance.last_updated)    // ISO timestamp`,
            },
          ],
        },
      },
      {
        id: 'wallet-transactions',
        title: 'Top Up & Deduct',
        searchKeywords: [
          'wallet',
          'topUp',
          'deduct',
          'credit',
          'debit',
          'transaction',
          'topUpWallet',
          'deductWallet',
        ],
        content: {
          type: 'simple',
          description: 'Add or remove credits from a wallet.',
          content: `topUpWallet() creates a credit transaction (adds balance). deductWallet() creates a debit transaction (removes balance). Both throw on failure and support idempotency keys to prevent duplicate transactions.`,
          codeExamples: [
            {
              language: 'typescript',
              description: 'Top up and deduct a wallet',
              code: `// Add 100 units to the wallet
const credit = await coomon.topUpWallet('wal_abc123', {
  amount: 100,
  creditType: 'purchased',           // 'free' | 'purchased' | 'promotional' | 'bonus'
  description: 'Monthly top-up',
  idempotencyKey: 'topup-invoice-001', // prevents duplicate charges
})
console.log(credit.credits_after) // 170

// Deduct 30 units from the wallet
const debit = await coomon.deductWallet('wal_abc123', {
  amount: 30,
  description: 'Usage charge',
  idempotencyKey: 'debit-session-xyz',
})
console.log(debit.credits_after) // 140`,
            },
            {
              language: 'typescript',
              description: 'Full wallet lifecycle example',
              code: `// Provision
const wallet = await coomon.createWallet({
  customerId: customer.id,
  name: 'Main Wallet',
  initialCredits: 0,
})

// Top up on payment
await coomon.topUpWallet(wallet.id, {
  amount: 100,
  creditType: 'purchased',
  idempotencyKey: \`topup-\${paymentId}\`,
})

// Check balance
const { credits_balance } = await coomon.getWalletBalance(wallet.id)
if (credits_balance < 10) {
  notifyLowBalance(customer.id)
}

// Deduct on usage
await coomon.deductWallet(wallet.id, {
  amount: usageCost,
  idempotencyKey: \`usage-\${sessionId}\`,
})`,
            },
          ],
        },
      },
    ],
  },
  {
    id: 'advanced',
    title: 'Advanced Features',
    items: [
      {
        id: 'serverless',
        title: 'Serverless & Edge',
        searchKeywords: [
          'serverless',
          'lambda',
          'edge',
          'vercel',
          'cloudflare',
          'immediate',
          'trackImmediate',
        ],
        content: {
          type: 'simple',
          description:
            'Use trackImmediate() in serverless environments where the process may die before the queue flushes.',
          content: `In serverless environments (AWS Lambda, Vercel Edge, Cloudflare Workers), the process can die before the queue flushes. Use the *Immediate methods to send events right away and await the HTTP response.`,
          codeExamples: [
            {
              language: 'typescript',
              description: 'AWS Lambda handler with trackImmediate',
              code: `import { Coomon } from '@coobird-ai/sdk'

const coomon = new Coomon(process.env.COOMON_API_KEY!, {
  host: 'https://app.coobird.ai',
  flushInterval: 0,  // disable background timer in serverless
})

export async function handler(event) {
  const result = await runAgent(event.body)

  await coomon.trackTaskImmediate({
    customerId: event.body.customerId,
    signalCode: 'agent_run',
    attributes: { model: result.model },
  })

  return { statusCode: 200, body: JSON.stringify(result) }
}`,
            },
            {
              language: 'typescript',
              description: 'All immediate methods available',
              code: `// Generic
await coomon.trackImmediate({ customerId: '...', actionType: 'task', ... })

// Typed wrappers (set actionType automatically)
await coomon.trackTaskImmediate({ customerId: '...', signalCode: '...' })
await coomon.trackWorkflowImmediate({ customerId: '...', signalCode: '...' })
await coomon.trackOutcomeImmediate({ customerId: '...', signalCode: '...' })`,
            },
          ],
        },
      },
      {
        id: 'global-attributes',
        title: 'Global Attributes',
        searchKeywords: [
          'register',
          'unregister',
          'global',
          'super properties',
          'attributes',
        ],
        content: {
          type: 'simple',
          description:
            'Set attributes that are merged into every event. Event-level attributes override globals.',
          content: `Use register() to set attributes merged into every event. This is useful for tagging all events with a common model, region, or service name. Event-level attributes override global ones.`,
          codeExamples: [
            {
              language: 'typescript',
              description: 'Register and unregister global attributes',
              code: `// Set once at startup
coomon.register({
  model: 'claude-3-5-sonnet',
  region: 'us-east-1',
  service: 'my-agent',
})

// All subsequent events include these attributes
coomon.trackTask({ customerId: 'cus_123' })
// -> attributes: { model: 'claude-3-5-sonnet', region: 'us-east-1', service: 'my-agent' }

// Override per-event
coomon.trackTask({
  customerId: 'cus_123',
  attributes: { model: 'gpt-4o' },  // overrides global 'model'
})

// Remove a global attribute
coomon.unregister('region')`,
            },
          ],
        },
      },
      {
        id: 'batching',
        title: 'Batching & Shutdown',
        searchKeywords: [
          'batch',
          'bulk',
          'flush',
          'shutdown',
          'graceful',
          'exit',
        ],
        content: {
          type: 'simple',
          description:
            'Automatic batching and graceful shutdown for long-running servers.',
          content: `Events are queued and sent in batches automatically. The queue flushes when it reaches flushAt events (default: 20) or every flushInterval ms (default: 10s). Call shutdown() before process exit to drain remaining events.`,
          codeExamples: [
            {
              language: 'typescript',
              description: 'Batching and graceful shutdown',
              code: `// Events are batched automatically — just call track()
coomon.trackTask({ customerId: 'cus_a', attributes: { action: 'email_sent' } })
coomon.trackTask({ customerId: 'cus_b', attributes: { action: 'email_sent' } })
coomon.trackOutcome({ customerId: 'cus_a', attributes: { outcome_type: 'meeting_booked' } })

// Flush manually (e.g. at end of a request)
await coomon.flush()

// Graceful shutdown — flush + stop timer. Call before process exit.
process.on('SIGTERM', async () => {
  await coomon.shutdown()
  process.exit(0)
})`,
            },
          ],
        },
      },
      {
        id: 'error-handling',
        title: 'Error Handling & Retries',
        searchKeywords: [
          'error',
          'retry',
          'failure',
          'resilience',
          'callbacks',
        ],
        content: {
          type: 'simple',
          description:
            'Built-in retry logic with exponential backoff for transient failures.',
          content: `The SDK retries intelligently: 429 (rate limited) and 5xx (server errors) are retried with exponential backoff (1s, 2s, 4s). Client errors (400, 401, 403, 404) are NOT retried. For queued events, errors go to onError. For direct calls (createCustomer, trackImmediate), errors are thrown.`,
          codeExamples: [
            {
              language: 'typescript',
              description: 'Error handling via onError callback',
              code: `const coomon = new Coomon(process.env.COOMON_API_KEY!, {
  host: 'https://app.coobird.ai',
  maxRetries: 3,          // retries: 1s -> 2s -> 4s exponential backoff
  onError: (err) => {
    console.error('Coobird SDK error:', err)
    // route to your logger, metrics, or alerting system
  },
})`,
            },
          ],
        },
      },
      {
        id: 'idempotency',
        title: 'Idempotency',
        searchKeywords: [
          'idempotent',
          'deduplicate',
          'duplicate',
          'eventId',
          'retry safe',
        ],
        content: {
          type: 'simple',
          description: 'Prevent duplicate billing with idempotency keys.',
          content: `Pass an eventId to prevent duplicate billing. The SDK auto-generates a UUID if omitted, but you can pass your own to safely retry failed calls.`,
          codeExamples: [
            {
              language: 'typescript',
              description: 'Using eventId for idempotency',
              code: `const eventId = crypto.randomUUID()

// Safe to retry — same eventId won't be billed twice
coomon.trackTask({
  customerId: 'cus_123',
  signalCode: 'api_call',
  eventId,
})`,
            },
          ],
        },
      },
    ],
  },
]
